import _ from "lodash";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";

import { headerTitleToId } from "./constants";
import { TableRow } from "../../constants/Styles";
import * as styledElements from "./FlashPCBMarkdownStyles";
import { findAndReplace } from "mdast-util-find-and-replace";
import { Nodes } from "mdast";

const Header1 = ({ ...props }) => <styledElements.Header1 {...props} />;
const Header2 = ({ ...props }) => (
  <styledElements.Header2
    id={headerTitleToId(props.children as string)}
    {...props}
  />
);
const Header3 = ({ ...props }) => <styledElements.Header3 {...props} />;
const Header4 = ({ ...props }) => <styledElements.Header4 {...props} />;
const Header5 = ({ ...props }) => <styledElements.Header5 {...props} />;
const Header6 = ({ ...props }) => <styledElements.Header6 {...props} />;
const Paragraph = ({ ...props }) => <styledElements.Paragraph {...props} />;
const Code = ({ ...props }) => (
  <styledElements.Code {..._.omit(props, ["inline"])} />
);
const UnorderedList = ({ ...props }) => (
  <styledElements.UnorderedList {..._.omit(props, ["ordered"])} />
);
const OrderedList = ({ ...props }) => (
  <styledElements.OrderedList {..._.omit(props, ["inline", "ordered"])} />
);
const ListItem = ({ ...props }) => (
  <styledElements.ListItem {..._.omit(props, ["ordered"])} />
);
const BlockQuote = ({ ...props }) => <styledElements.BlockQuote {...props} />;
const Table = ({ ...props }) => <styledElements.Table {...props} />;
const Row = ({ ...props }) => <TableRow {...props} />;
const Strong = ({ ...props }) => <styledElements.Strong {...props} />;

const MarkdownImage = ({ ...props }) => {
  const { src, alt } = props as { src: string; alt: string };
  return <styledElements.Image src={src} alt={alt} />;
};

const FlashPCBMarkdown = ({
  markdown,
  externalLinks,
  prettyTable = false,
  data,
}: {
  markdown: string;
  externalLinks?: (href: string) => boolean;
  prettyTable?: boolean;
  data?: { [key: string]: string };
}) => {
  const MarkdownLink = ({ ...props }) => {
    const { children, href } = props as { children: string[]; href: string };

    return (
      <styledElements.MarkdownLink
        href={href}
        passHref
        {...(externalLinks && externalLinks(href) ? { target: "_blank" } : {})}
        {..._.omit(props, ["children", "href"])}
      >
        {children}
      </styledElements.MarkdownLink>
    );
  };

  const TableHeader = ({ ...props }) => (
    <styledElements.TableHeader
      borderBottom={prettyTable}
      borderRight={prettyTable}
      {...props}
    />
  );

  const Cell = ({ ...props }) => (
    <styledElements.Cell borderRight={prettyTable} {...props} />
  );

  function myRemarkPlugin() {
    /**
     * @param {import('mdast').Root} tree
     *   Tree.
     * @returns {undefined}
     *   Nothing.
     */
    return function (tree: Nodes) {
      findAndReplace(tree, [
        /:replace\[(([.a-zA-Z0-9_]*)|(\[\d+]))*]/g,
        (__, ___, key: string) => {
          const replacement = data?.[key];
          if (replacement === undefined) {
            throw Error(`Missing variable: ${key}`);
          }
          return replacement;
        },
      ]);
    };
  }

  return (
    <div>
      <ReactMarkdown
        components={{
          a: MarkdownLink,
          h1: Header1,
          h2: Header2,
          h3: Header3,
          h4: Header4,
          h5: Header5,
          h6: Header6,
          p: Paragraph,
          code: Code,
          ol: OrderedList,
          ul: UnorderedList,
          li: ListItem,
          blockquote: BlockQuote,
          table: Table,
          th: TableHeader,
          tr: Row,
          td: Cell,
          strong: Strong,
          img: MarkdownImage,
        }}
        remarkPlugins={[remarkGfm, myRemarkPlugin]}
        rehypePlugins={[rehypeRaw]}
      >
        {markdown}
      </ReactMarkdown>
    </div>
  );
};

export default FlashPCBMarkdown;
